import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../api";
import {
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent
} from "@mui/material";

import { hubConnection } from "../signalr";
import StatusBadge from "../components/StatusBadge";
import JsonViewer from "../components/JsonViewer";
import Heartbeat from "../components/Heartbeat";
import LiveEventStream from "../components/LiveEventStream";

export default function DeviceDetails() {
  const { deviceId } = useParams();

  const [device, setDevice] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [commands, setCommands] = useState([]);
  const [events, setEvents] = useState([]);
  const [history, setHistory] = useState([]);

  const [selectedCommand, setSelectedCommand] = useState(null);

  useEffect(() => {
    api.get("/device/list").then(res => {
      const found = res.data.find(d => d.deviceId === deviceId);
      setDevice(found);
    });

    api.get(`/policy/assigned/${deviceId}`).then(res => setPolicies(res.data));
    api.get(`/command/pending/${deviceId}`).then(res => setCommands(res.data));
    api.get(`/event/history/${deviceId}`).then(res => setEvents(res.data));
    api.get(`/command/history/${deviceId}`).then(res => setHistory(res.data));

    // ⭐ REAL-TIME LISTENERS
    hubConnection.on("deviceCheckIn", updated => {
      if (updated.deviceId === deviceId) setDevice(updated);
    });

    hubConnection.on("eventLogged", evt => {
      if (evt.deviceId === deviceId) setEvents(prev => [evt, ...prev]);
    });

    hubConnection.on("commandQueued", cmd => {
      if (cmd.deviceId === deviceId) {
        setCommands(prev =>
          prev.find(c => c.id === cmd.id) ? prev : [...prev, cmd]
        );

        setHistory(prev =>
          prev.find(h => h.id === cmd.id) ? prev : [cmd, ...prev]
        );
      }
    });

    hubConnection.on("commandCompleted", cmd => {
      if (cmd.deviceId === deviceId) {
        setCommands(prev => prev.filter(c => c.id !== cmd.id));

        setHistory(prev =>
          prev.some(h => h.id === cmd.id)
            ? prev.map(h => (h.id === cmd.id ? cmd : h))
            : [cmd, ...prev]
        );
      }
    });

    hubConnection.on("commandFailed", cmd => {
      if (cmd.deviceId === deviceId) {
        setCommands(prev => prev.filter(c => c.id !== cmd.id));

        setHistory(prev =>
          prev.some(h => h.id === cmd.id)
            ? prev.map(h => (h.id === cmd.id ? cmd : h))
            : [cmd, ...prev]
        );
      }
    });

    hubConnection.on("commandCancelled", cmd => {
      if (cmd.deviceId === deviceId) {
        setCommands(prev => prev.filter(c => c.id !== cmd.id));

        setHistory(prev =>
          prev.some(h => h.id === cmd.id)
            ? prev.map(h => (h.id === cmd.id ? cmd : h))
            : [cmd, ...prev]
        );
      }
    });

    return () => {
      hubConnection.off("deviceCheckIn");
      hubConnection.off("eventLogged");
      hubConnection.off("commandQueued");
      hubConnection.off("commandCompleted");
      hubConnection.off("commandFailed");
      hubConnection.off("commandCancelled");
    };
  }, [deviceId]);

  if (!device) return <p>Loading...</p>;

  // -----------------------------
  // COMMAND ACTIONS
  // -----------------------------
  const sendCommand = (commandName) => {
    api.post("/command/queue", {
      deviceId: device.id,
      commandName
    });
  };

  const completeCommand = (commandId) => {
    api.post("/command/complete", { id: commandId });
  };

  const cancelCommand = (commandId) => {
    api.post("/command/cancel", commandId);
  };

  const retryCommand = (commandId) => {
    api.post("/command/retry", commandId);
  };

  return (
    <div style={{ padding: 20 }}>
      <Typography variant="h4" gutterBottom>
        Device: {device.name}
      </Typography>

      {/* DEVICE INFO */}
      <Card style={{ marginBottom: 20 }}>
        <CardContent>
          <Typography><strong>Device ID:</strong> {device.deviceId}</Typography>
          <Typography><strong>Platform:</strong> {device.platform}</Typography>
          <Typography>
            <strong>Last Check-In:</strong> {device.lastCheckIn || "Never"}
          </Typography>
          <Typography style={{ marginTop: 5 }}>
            <strong>Status:</strong> <Heartbeat lastCheckIn={device.lastCheckIn} />
          </Typography>
        </CardContent>
      </Card>

      {/* COMMAND BUTTONS */}
      <Typography variant="h5" gutterBottom>Commands</Typography>

      <Button variant="contained" color="primary" onClick={() => sendCommand("sync")} style={{ marginRight: 10 }}>
        Sync
      </Button>

      <Button variant="contained" color="secondary" onClick={() => sendCommand("lock")} style={{ marginRight: 10 }}>
        Lock Device
      </Button>

      <Button variant="contained" color="error" onClick={() => sendCommand("wipe")}>
        Wipe Device
      </Button>

      {/* PENDING COMMANDS */}
      <Typography variant="h5" gutterBottom style={{ marginTop: 30 }}>
        Pending Commands
      </Typography>

      {commands.length === 0 ? (
        <p>No pending commands.</p>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Command</TableCell>
              <TableCell>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {commands.map(c => (
              <TableRow key={c.id}>
                <TableCell>{c.commandName}</TableCell>
                <TableCell>{c.createdAt}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* POLICIES */}
      <Typography variant="h5" gutterBottom style={{ marginTop: 30 }}>
        Assigned Policies
      </Typography>

      {policies.length === 0 ? (
        <p>No policies assigned.</p>
      ) : (
        policies.map((p, index) => (
          <Card key={`${p.id}-${index}`} style={{ marginBottom: 10 }}>
            <CardContent>
              <Typography><strong>{p.policyName}</strong></Typography>
              <pre>{p.jsonDefinition}</pre>
            </CardContent>
          </Card>
        ))
      )}

      {/* EVENT LOGS */}
      <Typography variant="h5" gutterBottom style={{ marginTop: 30 }}>
        Event Logs
      </Typography>

      {events.length === 0 ? (
        <p>No events logged.</p>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Event</TableCell>
              <TableCell>Timestamp</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map(e => (
              <TableRow key={e.id}>
                <TableCell>{e.eventType}</TableCell>
                <TableCell>{e.createdAt}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* LIVE EVENT STREAM */}
      <Typography variant="h6" gutterBottom style={{ marginTop: 20 }}>
        Live Event Stream
      </Typography>

      <LiveEventStream deviceId={deviceId} />

      {/* COMMAND HISTORY */}
      <Typography variant="h5" gutterBottom style={{ marginTop: 30 }}>
        Command History
      </Typography>

      {history.length === 0 ? (
        <p>No command history.</p>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Command</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Completed</TableCell>
              <TableCell>Result</TableCell>
              <TableCell>Error</TableCell>
              <TableCell>Agent Version</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {history.map(h => (
              <TableRow key={h.id} onClick={() => setSelectedCommand(h)} style={{ cursor: "pointer" }}>
                <TableCell>{h.commandName}</TableCell>

                <TableCell>
                  <StatusBadge status={h.status} />
                </TableCell>

                <TableCell>{h.createdAt}</TableCell>
                <TableCell>{h.completedAt || "—"}</TableCell>

                <TableCell>
                  <JsonViewer json={h.resultPayload} />
                </TableCell>

                <TableCell style={{ color: h.errorMessage ? "red" : "inherit" }}>
                  {h.errorMessage || "—"}
                </TableCell>

                <TableCell>{h.agentVersion || "—"}</TableCell>

                <TableCell>
                  {h.status === "Pending" && (
                    <>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          cancelCommand(h.id);
                        }}
                        style={{ marginRight: 8 }}
                      >
                        Cancel
                      </Button>

                      <Button
                        variant="outlined"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          completeCommand(h.id);
                        }}
                      >
                        Mark Complete
                      </Button>
                    </>
                  )}

                  {h.status === "Failed" && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        retryCommand(h.id);
                      }}
                    >
                      Retry
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* MODAL */}
      <Dialog open={!!selectedCommand} onClose={() => setSelectedCommand(null)} maxWidth="md" fullWidth>
        <DialogTitle>Command Details</DialogTitle>
        <DialogContent>
          {selectedCommand && (
            <>
              <Typography><strong>Command:</strong> {selectedCommand.commandName}</Typography>
              <Typography><strong>Status:</strong> {selectedCommand.status}</Typography>
              <Typography><strong>Created:</strong> {selectedCommand.createdAt}</Typography>
              <Typography><strong>Completed:</strong> {selectedCommand.completedAt || "—"}</Typography>
              <Typography><strong>Agent Version:</strong> {selectedCommand.agentVersion || "—"}</Typography>

              <Typography style={{ marginTop: 10 }}><strong>Error:</strong></Typography>
              <pre style={{ color: "red" }}>{selectedCommand.errorMessage || "—"}</pre>

              <Typography style={{ marginTop: 10 }}><strong>Result Payload:</strong></Typography>
              <JsonViewer json={selectedCommand.resultPayload} />
            </>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}
